import {
  Alert,
  Button,
  Code,
  createStyles,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as net from 'util/Net';

import type {BoltzApiKeys} from './BoltzApiService';
import {
  BOLTZ_CREDIT_INCREMENT,
  BOLTZ_MAX_CREDITS_PER_TRANSFER,
  provisionBoltzApiKeys,
  transferBoltzCredits,
} from './BoltzApiService';

const TEST_COMPONENT = 'BoltzApiConfigureModal';

const useStyles = createStyles(() => ({
  apiKeyValue: {
    flex: 1,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
}));

export interface BoltzApiConfigureModalProps {
  opened: boolean;
  onClose: () => void;
}

function formatCredits(credits: number): string {
  return credits.toLocaleString();
}

export function buildTransferOptions(): Array<{value: string; label: string}> {
  const options: Array<{value: string; label: string}> = [];
  for (
    let credits = BOLTZ_CREDIT_INCREMENT;
    credits <= BOLTZ_MAX_CREDITS_PER_TRANSFER;
    credits += BOLTZ_CREDIT_INCREMENT
  ) {
    options.push({value: String(credits), label: `${formatCredits(credits)} credits`});
  }
  return options;
}

const TRANSFER_OPTIONS = buildTransferOptions();

const HTTP_STATUS_PAYMENT_REQUIRED = 402;

function getTransferErrorMessage(e: unknown): string {
  // The backend returns a 402 when the tenant doesn't have enough Benchling credits for the transfer.
  if (net.isServerRequestError(e) && (e as {status?: number}).status === HTTP_STATUS_PAYMENT_REQUIRED) {
    return net.getUserMessage(e) ?? 'You don’t have enough Benchling credits to complete this transfer.';
  }
  return net.getUserMessage(e) ?? 'We couldn’t complete the transfer. Please try again.';
}

function ApiKeyRow({
  label,
  value,
  copyLabel,
}: {
  label: string;
  value: string;
  copyLabel: string;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Stack spacing={4}>
      <Text size="sm" weight={600}>
        {label}
      </Text>
      <Group spacing="xs" noWrap={true} align="center">
        <Code
          block={true}
          className={classes.apiKeyValue}
          {...getTestProps({component: TEST_COMPONENT, element: 'apiKeyValue', key: label})}
        >
          {value}
        </Code>
        <Button
          size="xs"
          variant="secondary"
          aria-label={copyLabel}
          onClick={() => {
            copyToClipboard(value, {showToastOnSuccess: true, toastText: `${label} copied`}).catch(() => {});
          }}
        >
          Copy
        </Button>
      </Group>
    </Stack>
  );
}

export function BoltzApiConfigureModal({opened, onClose}: BoltzApiConfigureModalProps): JSX.Element {
  const [selectedCredits, setSelectedCredits] = useState<string>(String(BOLTZ_CREDIT_INCREMENT));
  const [isConfirming, setIsConfirming] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);

  const [apiKeys, setApiKeys] = useState<BoltzApiKeys | null>(null);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setSelectedCredits(String(BOLTZ_CREDIT_INCREMENT));
    setIsConfirming(false);
    setTransferring(false);
    setTransferError(null);
    setTransferSuccess(null);
    setApiKeys(null);
    setGeneratingKeys(false);
    setKeysError(null);
  }, [opened]);

  const handleStartTransfer = (): void => {
    setTransferError(null);
    setTransferSuccess(null);
    setIsConfirming(true);
  };

  const handleConfirmTransfer = (): void => {
    const credits = Number(selectedCredits);
    setTransferring(true);
    setTransferError(null);
    transferBoltzCredits(credits)
      .then(() => {
        setTransferSuccess(`${formatCredits(credits)} Benchling credits were transferred to the Boltz API.`);
        setIsConfirming(false);
        setTransferring(false);
      })
      .catch((e: unknown) => {
        setTransferError(getTransferErrorMessage(e));
        setIsConfirming(false);
        setTransferring(false);
      });
  };

  const handleGenerateKeys = (): void => {
    setGeneratingKeys(true);
    setKeysError(null);
    provisionBoltzApiKeys()
      .then((keys) => {
        setApiKeys(keys);
        setGeneratingKeys(false);
      })
      .catch((e: unknown) => {
        setKeysError(net.getUserMessage(e) ?? 'We couldn’t generate API keys. Please try again.');
        setGeneratingKeys(false);
      });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Configure Boltz API" size="lg">
      <Stack spacing="lg" {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
        <Stack spacing="sm">
          <Title order={5}>Transfer Benchling credits</Title>
          <Text size="sm" color="dimmed">
            Transfer Benchling credits to the Boltz API to fund usage. Credits are transferred in increments
            of {formatCredits(BOLTZ_CREDIT_INCREMENT)}, up to {formatCredits(BOLTZ_MAX_CREDITS_PER_TRANSFER)}{' '}
            at a time. This is a one-time transfer and can’t be reversed.
          </Text>

          {transferSuccess != null && (
            <Alert color="green" variant="light">
              {transferSuccess}
            </Alert>
          )}
          {transferError != null && (
            <Alert color="red" variant="light">
              {transferError}
            </Alert>
          )}

          {!isConfirming && (
            <Group align="flex-end" spacing="sm">
              <Select
                label="Credits to transfer"
                data={TRANSFER_OPTIONS}
                value={selectedCredits}
                onChange={(value) => {
                  if (value != null) {
                    setSelectedCredits(value);
                  }
                }}
                withinPortal={true}
                {...getTestProps({component: TEST_COMPONENT, element: 'creditsSelect'})}
              />
              <Button onClick={handleStartTransfer}>Transfer credits</Button>
            </Group>
          )}

          {isConfirming && (
            <Alert color="orange" variant="light" title="Confirm credit transfer">
              <Stack spacing="sm">
                <Text size="sm">
                  You’re about to transfer {formatCredits(Number(selectedCredits))} Benchling credits to the
                  Boltz API. This is a one-time transfer and can’t be reversed.
                </Text>
                <Group spacing="sm">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsConfirming(false);
                    }}
                    disabled={transferring}
                  >
                    Cancel
                  </Button>
                  <Button color="orange" loading={transferring} onClick={handleConfirmTransfer}>
                    Confirm transfer
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
        </Stack>

        <Divider />

        <Stack spacing="sm">
          <Title order={5}>API keys</Title>
          <Text size="sm" color="dimmed">
            Generate API keys to authenticate your requests to the Boltz API.
          </Text>

          {keysError != null && (
            <Alert color="red" variant="light">
              {keysError}
            </Alert>
          )}

          {apiKeys == null ? (
            <Group>
              <Button loading={generatingKeys} onClick={handleGenerateKeys}>
                Generate API key
              </Button>
            </Group>
          ) : (
            <Stack spacing="md">
              <Alert color="yellow" variant="light" title="Save your keys now">
                Copy and store these keys somewhere safe. For your security, you won’t be able to view them
                again after you close this modal. To revoke old keys, contact support.
              </Alert>
              <ApiKeyRow label="Live API key" value={apiKeys.liveApiKey} copyLabel="Copy live API key" />
              <ApiKeyRow label="Test API key" value={apiKeys.testApiKey} copyLabel="Copy test API key" />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
